import { exponentialBackoffMs, parseRetryAfterMs, sleep } from '@developerz/kit';

// The reconnect pacing every SSE consumer in this app shares.
//
// All three live views are `fetch` + a stream reader rather than `EventSource`
// (the `dev_pat_` bearer has to ride along), which means the reconnection
// behaviour `EventSource` gives for free is ours to implement — including the
// spec's `retry:` field, which `apps/api/src/middleware/sse-stream.ts` publishes
// once per connection so every client of ours paces the same.
//
// WHY THERE IS A LADDER AT ALL. `retry:` alone is a CONSTANT, and a constant is
// what makes an api roll a self-inflicted outage. Every open tab holds at least
// one of these connections (`NotificationBell` sits in `AppTopbar`), so a pod
// recycle drops them all at the same instant and a fixed 3 s re-dial brings them
// all back at the same instant, together, forever. The read bucket is 60/min per
// token and each attempt spends one, so a tab re-dialling every 3 s spends 20 of
// them a minute doing nothing but reconnecting — and a 429 answered at the same
// fixed pace is a loop that cannot end, because the thing keeping the budget
// exhausted is the retrying.
//
// Two properties fix that, and they are different properties:
//
//   - **Growth** bounds the spend. Consecutive failures widen the window, so a
//     client that cannot get in stops asking 20 times a minute.
//   - **Jitter** breaks the lockstep. Without it, growth alone just moves the
//     whole herd to the same later instant.
//
// `Retry-After` outranks both: a server that TELLS you when to come back and a
// client that ignores it is precisely how a rate limit becomes an outage
// (`@developerz/kit` `backoff.ts`, the same rule for the same reason).

/** Fallback when a connection published no `retry:` — the API's `SSE_RETRY_MS`. */
export const DEFAULT_SSE_RETRY_MS = 3_000;

/**
 * Ceiling on a single reconnect wait. The account stream recycles every ~10 min
 * by design, so a wait longer than that would leave a tab dark past the point
 * where a healthy connection would have been recycled anyway.
 */
export const SSE_BACKOFF_CAP_MS = 60_000;

/**
 * Ceiling on a `Retry-After` we will actually honour. Our own limiter answers
 * seconds derived from its window, so this only bounds a mangled or hostile
 * value: a proxy rewriting the header must not be able to park a live view for
 * a day. Ten minutes is the stream's own recycle window — past it there is
 * nothing to resume into anyway.
 */
export const SSE_RETRY_AFTER_CAP_MS = 10 * 60_000;

/**
 * How long a connection must STAY open to count as having "come up" — the
 * event that resets the ladder (`sseSubscribe`). Not the open itself: a pod
 * that accepts and dies at once (crash-looping, a proxy answering 200 then
 * closing) would pin every tab at the first rung, re-dialling at the base pace
 * forever — exactly the spend growth exists to bound. Six heartbeats
 * (`middleware/sse-stream.ts` beats every 5 s) is proof the path carries a live
 * stream, and it is a twentieth of the ~10-min recycle, so a tab dropped by
 * sleep/wake or a proxy idle timeout is back at the first rung on its next drop.
 * A flapping server is held to one dial per 30 s plus its climbing wait.
 */
export const SSE_HEALTHY_OPEN_MS = 30_000;

/** What the next reconnect wait is computed from. Pure inputs, injected clock-free. */
export type ReconnectPacing = {
  /**
   * Consecutive connections that failed since the last one that came up
   * (stayed open {@link SSE_HEALTHY_OPEN_MS}) or was recycled. 0-based.
   */
  readonly attempt: number;
  /** The pace the SERVER published (`retry:`), or {@link DEFAULT_SSE_RETRY_MS}. */
  readonly baseMs: number;
  /** `Retry-After`, in ms, when the server named one. See {@link retryAfterMs}. */
  readonly retryAfterMs: number | null;
  /** `Math.random` in the app; a fixed draw in tests. */
  readonly random: () => number;
};

/**
 * `Retry-After` off a refusal, in milliseconds, or `null` when the server named
 * none. Both RFC 9110 spellings, through `@developerz/kit`'s one parser
 * (`parseRetryAfterMs`): delta-seconds as `1*DIGIT` only — `Number()` read
 * `1e3` as 1000 s — and an IMF-fixdate dated against `nowMs`.
 *
 * Our own limiter emits delta-seconds (`middleware/rate-limit-headers.ts`), but
 * a proxy in front of it may answer with a date, and dropping that silently is
 * how a rate limit becomes a reconnect spin. A browser clock that runs ahead can
 * date the wait to zero or below; that is read as "no instruction" (`null`), so
 * the ladder paces the retry instead — a skewed clock costs the exact wait, never
 * a spin.
 */
export function retryAfterMs(res: Response, nowMs: number = Date.now()): number | null {
  const ms = parseRetryAfterMs(res.headers.get('retry-after'), nowMs);
  if (ms === null || ms <= 0) return null;
  return Math.min(ms, SSE_RETRY_AFTER_CAP_MS);
}

/**
 * How long to wait before the next connection attempt.
 *
 * `Retry-After` wins outright and jitter rides ON TOP of it: the server's number
 * is a floor we must not undercut, but honouring it to the millisecond puts
 * every refused tab back on the socket at the same instant, which is the same
 * herd with an extra step.
 *
 * Otherwise the window is exponential over the SERVER's published pace —
 * `exponentialBackoffMs` from `@developerz/kit`, so the math has one
 * implementation — and the wait is drawn from the top half of it (EQUAL jitter,
 * not full). Full jitter draws from `[0, window)`, which can return ~0 and put a
 * client that was just refused straight back on the socket; what this needs is
 * decorrelation, not a uniform draw from zero. The top half still spreads a herd
 * across half the window — 1.5 s at the first attempt, 30 s at the cap — and is
 * monotone in `attempt` for a fixed draw, which is what makes the ladder
 * assertable at all.
 */
export function reconnectDelayMs(p: ReconnectPacing): number {
  const draw = Math.min(Math.max(p.random(), 0), 1);
  if (p.retryAfterMs !== null) return p.retryAfterMs + draw * p.baseMs;
  const window = exponentialBackoffMs(p.attempt, {
    baseMs: p.baseMs,
    capMs: SSE_BACKOFF_CAP_MS,
  });
  return window / 2 + draw * (window / 2);
}

/**
 * Sleep `ms`, returning early (and clearing its timer) the moment `signal`
 * aborts. A plain `setTimeout` would hold a reconnect pending across an unmount
 * or an org switch, then reopen a stream nobody is watching.
 *
 * The timer, the listener and the abort race are `@developerz/kit`'s one
 * {@link sleep}. What stays here is the CONTRACT both callers were written
 * against: this RESOLVES on abort where `sleep` REJECTS. Each reconnect loop
 * awaits it and then re-reads `signal.aborted` to decide whether to return, so
 * letting the rejection through would escape the loop's `try` and surface as an
 * unhandled rejection on every unmount and every org switch.
 */
export function abortableDelay(ms: number, signal: AbortSignal): Promise<void> {
  return sleep(ms, signal).catch(() => {});
}
